import { useMemo, useState } from "react";
import type { AppState, ProjectEntry, Session, SessionStatusInfo } from "../types";
import { cx, formatRelativeTime } from "../util";

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  user: string;
  theme: "light" | "dark" | "system";
  onToggleTheme: () => void;
  onLogout: () => void;
  onOpenSettings: () => void;
  projects: ProjectEntry[];
  directory: string;
  onDirectoryChange: (dir: string) => void;
  sessions: Session[];
  sessionStatus: Record<string, SessionStatusInfo>;
  activeSessionId: string | null;
  onSelectSession: (id: string) => void;
  onNewSession: () => void;
  onRenameSession: (id: string) => void;
  onDeleteSession: (id: string) => void;
  search: string;
  onSearch: (term: string) => void;
  appState: AppState;
  onToggleFavorite: (id: string) => void;
}

function isRunning(status: SessionStatusInfo | undefined): boolean {
  return !!status && status.type !== "idle";
}

export function Sidebar(props: SidebarProps) {
  const [filter, setFilter] = useState("");
  const {
    sessions,
    favorites,
  } = { sessions: props.sessions, favorites: props.appState.favorites };

  const favSet = useMemo(() => new Set(favorites), [favorites]);
  const visible = useMemo(() => {
    const term = (filter || props.search).toLowerCase();
    return sessions.filter((s) => !term || (s.title ?? s.slug ?? s.id).toLowerCase().includes(term));
  }, [sessions, filter, props.search]);

  const favoritesOnly = visible.filter((s) => favSet.has(s.id));
  const running = visible.filter((s) => isRunning(props.sessionStatus[s.id]));

  function renderSession(s: Session) {
    const title = s.title || s.slug || "Sessão sem título";
    return (
      <div
        key={s.id}
        className={cx("session-item", props.activeSessionId === s.id && "active")}
        role="button"
        tabIndex={0}
        aria-current={props.activeSessionId === s.id}
        onClick={() => props.onSelectSession(s.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") props.onSelectSession(s.id);
        }}
      >
        <button
          className={cx("star", favSet.has(s.id) && "on")}
          aria-label={favSet.has(s.id) ? "Remover dos favoritos" : "Adicionar aos favoritos"}
          title={favSet.has(s.id) ? "Remover dos favoritos" : "Adicionar aos favoritos"}
          onClick={(e) => {
            e.stopPropagation();
            props.onToggleFavorite(s.id);
          }}
        >
          {favSet.has(s.id) ? "★" : "☆"}
        </button>
        <span className="title" title={title}>
          {title}
        </span>
        {isRunning(props.sessionStatus[s.id]) ? (
          <span className="badge run" title="Execução em andamento">
            ●
          </span>
        ) : null}
        <span className="meta">{formatRelativeTime(s.time?.updated ?? s.time?.created)}</span>
        <button
          className="icon-btn"
          aria-label="Renomear sessão"
          title="Renomear sessão"
          onClick={(e) => {
            e.stopPropagation();
            props.onRenameSession(s.id);
          }}
        >
          ✎
        </button>
        <button
          className="icon-btn"
          aria-label="Excluir sessão"
          title="Excluir sessão"
          onClick={(e) => {
            e.stopPropagation();
            props.onDeleteSession(s.id);
          }}
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <nav className={cx("sidebar", props.open && "open")} aria-label="Navegação principal">
      <div className="brand">
        <div className="brand-logo" aria-hidden>
          OC
        </div>
        <div>
          <div className="brand-title">Opencode Cloud</div>
          <div className="brand-sub">motor OpenCode · sessões reais</div>
        </div>
      </div>

      <div className="side-section">
        <div className="field" style={{ marginBottom: 2 }}>
          <label htmlFor="project-select">Projeto</label>
          <select
            id="project-select"
            className="select"
            style={{ maxWidth: "100%" }}
            value={props.directory}
            onChange={(e) => props.onDirectoryChange(e.target.value)}
          >
            {props.projects.map((p) => (
              <option key={p.path} value={p.path}>
                {p.name} — {p.path}
              </option>
            ))}
          </select>
        </div>
        <button className="btn primary" onClick={props.onNewSession}>
          + Nova sessão
        </button>
        <div className="search-box">
          <span aria-hidden>🔎</span>
          <input
            aria-label="Pesquisar sessões"
            placeholder="Pesquisar sessões…  (Ctrl+K)"
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              props.onSearch(e.target.value);
            }}
          />
        </div>
      </div>

      <div className="session-list">
        {running.length > 0 && (
          <>
            <div className="side-label">
              <span>Em execução</span>
              <span className="badge run">{running.length}</span>
            </div>
            {running.map(renderSession)}
          </>
        )}

        {favoritesOnly.length > 0 && (
          <>
            <div className="side-label">
              <span>Favoritos</span>
            </div>
            {favoritesOnly.map(renderSession)}
          </>
        )}

        <div className="side-label">
          <span>Sessões</span>
          <span className="badge">{visible.length}</span>
        </div>
        {visible.length === 0 ? (
          <p style={{ color: "var(--text-faint)", fontSize: 13, padding: "4px 8px" }}>
            Nenhuma sessão encontrada. Crie uma nova sessão para começar.
          </p>
        ) : (
          visible.map(renderSession)
        )}
      </div>

      <div className="sidebar-footer">
        <button className="btn ghost sm" onClick={props.onOpenSettings} title="Configurações do OpenCode">
          ⚙ Configurações
        </button>
        <div style={{ flex: 1 }} />
        <button
          className="icon-btn"
          onClick={props.onToggleTheme}
          aria-label="Alternar tema claro/escuro"
          title="Alternar tema (claro/escuro)"
        >
          {props.theme === "dark" ? "☀" : "☾"}
        </button>
        <button className="btn ghost sm" onClick={props.onLogout} title={`Sair da conta ${props.user}`}>
          Sair
        </button>
      </div>
    </nav>
  );
}
